// Mapeamento loja → empresa (CNPJ) do Grupo Correia MC.
// Fonte única, reutilizada nas telas de Consulta e Relatório da Pesquisa de Clima.
// Os códigos de loja batem com os que saem de normalizarLoja (server/utils.js): PCI, PCS, etc.

// Empresa → lojas que pertencem a ela.
// Cada empresa lista TODOS os códigos que a representam, incluindo os "apelidos"
// longos que a dtbConfere guarda (ex.: 'PCI P&M', 'PCS GC') além do código
// canônico de 3 letras ('PCI', 'PCS'). Assim o índice reverso reconhece as duas
// formas. (Antes 'P&M'/'GC FOOD'/'PARTNER' estavam duplicadas como chaves — a 2ª
// sobrescrevia a 1ª em silêncio e 'PCI'/'PCS'/'PARTNER' ficavam sem empresa;
// corrigido em 2026-06-12 ao juntar as listas.)
export const LOJAS_POR_EMPRESA = {
    HUNGER:    ['ANV', 'FER', 'GTV', 'RDP', 'SED', 'VEL'],
    'P&G':     ['BVV', 'MAL', 'MOX', 'SVV', 'VSH'],
    'P&M':     ['PCI', 'PCI P&M'],
    'GC FOOD': ['PCS', 'PCS GC'],
    'P&M GALLI' : ['LIN'],
    'PARTNER' : ['PARTNER', 'ESCRITORIO'],
    'PGO' : ['PGO'],
    'PSM' : ['PSM'],
    PRT:       ['PRT'],
    PTN:       ['PTN'],
};

// Índice reverso loja → empresa
export const EMPRESA_POR_LOJA = Object.fromEntries(
    Object.entries(LOJAS_POR_EMPRESA).flatMap(([empresa, lojas]) => lojas.map(loja => [loja, empresa]))
);

// Loja não mapeada (ex.: loja nova, LOJA_TESTE_CARGA) cai em 'Outras'
export const getEmpresa = (loja) => EMPRESA_POR_LOJA[loja] || 'Outras';

// Empresas que NÃO participam dos formulários do módulo Manutenção
// (Certificação de Equipamentos e Conservação Predial) — decisão de 2026-07-23.
// Ficam de fora: PARTNER (+ ESCRITÓRIO), PGO, PSM, PRT e PTN. Continuam valendo
// HUNGER, P&G, P&M, GC FOOD e P&M GALLI (LIN).
export const EMPRESAS_FORA_MANUTENCAO = ['PARTNER', 'PGO', 'PSM', 'PRT', 'PTN'];

// A loja entra nos formulários de Manutenção? Filtra pela EMPRESA da loja
// (getEmpresa já reconhece tanto o código canônico 'PCI' quanto o apelido
// longo 'PCI P&M' da dtbConfere).
export const ehLojaManutencao = (loja) => !EMPRESAS_FORA_MANUTENCAO.includes(getEmpresa(loja));

// Lista ESTÁTICA de lojas (código canônico) que entram na Manutenção — as das
// empresas participantes (todas menos EMPRESAS_FORA_MANUTENCAO). Descarta os
// apelidos longos com espaço ('PCI P&M', 'PCS GC'), como LOJAS_AUDITAVEIS.
// Usada no dropdown de CADASTRO de equipamentos (Certificação), que não tem
// lista dinâmica da dtbConfere. Diferença para LOJAS_AUDITAVEIS: inclui LIN
// (P&M GALLI). Exclui PARTNER/ESCRITÓRIO, PGO, PSM, PRT, PTN.
export const LOJAS_MANUTENCAO = [...new Set(
  Object.entries(LOJAS_POR_EMPRESA)
    .filter(([empresa]) => !EMPRESAS_FORA_MANUTENCAO.includes(empresa))
    .flatMap(([, lojas]) => lojas),
)]
  .filter((l) => !l.includes(' '))
  .sort();

// Empresas operadas pelo grupo cujas lojas entram no módulo de Auditoria
// (formulário de checklist e relatórios). Fonte única reutilizada por ambos.
export const EMPRESAS_AUDITAVEIS = ['HUNGER', 'P&G', 'P&M', 'GC FOOD'];

// Lista de lojas auditáveis, derivada das empresas acima (13 lojas).
// Usa só os códigos CANÔNICOS (3 letras) — descarta os apelidos longos com espaço
// ('PCI P&M', 'PCS GC'). Importante: o dropdown de loja do checklist precisa do
// código canônico para casar com a chave dos gerentes (que vem de normalizarLoja)
// e com o LOJA gravado na auditoria.
export const LOJAS_AUDITAVEIS = [...new Set(
  EMPRESAS_AUDITAVEIS.flatMap((e) => LOJAS_POR_EMPRESA[e] || [])
)]
  .filter((l) => !l.includes(' '))
  .sort();

// Mercado (região operacional) de cada loja auditável — Norte × Sul.
// Usado no módulo Operacional: cada consultor avalia o mercado do OUTRO
// (cross-avaliação, para impessoalidade). Este é o espelho de UX (dropdowns e
// filtros); a FONTE DE VERDADE que AUTORIZA é o backend
// (server/operacional/consultor-mercado.js, + a coluna dtbCadastroLojas.mercado).
// Se divergirem, quem manda é o backend.
// ⚠️ NÃO deriva da empresa: HUNGER e P&G têm lojas nos dois mercados
// (ANV/FER/RDP/SED = Norte, mas GTV/VEL, da mesma HUNGER, = Sul).
// As 13 chaves são exatamente as LOJAS_AUDITAVEIS (Norte 7 / Sul 6).
export const MERCADO_POR_LOJA = {
  // Norte (7)
  PCI: 'Norte', VSH: 'Norte', RDP: 'Norte', FER: 'Norte', ANV: 'Norte', MAL: 'Norte', SED: 'Norte',
  // Sul (6)
  GTV: 'Sul', BVV: 'Sul', SVV: 'Sul', PCS: 'Sul', VEL: 'Sul', MOX: 'Sul',
};

// Mercado de uma loja (código canônico), ou null se não for auditável.
export const getMercado = (loja) => MERCADO_POR_LOJA[loja] || null;

// Lojas de um mercado (código canônico), na ordem de LOJAS_AUDITAVEIS.
export const lojasDoMercado = (mercado) =>
  LOJAS_AUDITAVEIS.filter((l) => MERCADO_POR_LOJA[l] === mercado);

// Consultor (ID em dtbUsuariosSite) → mercado que ELE avalia (cross-avaliação):
// ID 21 (do Sul) avalia o Norte; ID 22 (do Norte) avalia o Sul. Espelho de UX do
// backend (server/operacional/consultor-mercado.js › CONSULTOR_MERCADO), que é
// quem AUTORIZA. Manter em sincronia — mexeu aqui, confira lá.
export const CONSULTOR_MERCADO = { 21: 'Norte', 22: 'Sul' };

// Mercado que o usuário avalia, ou null se não é consultor designado (Admin e
// perfis amplos → null = sem trava, veem os dois mercados).
export const mercadoDoConsultor = (userId) => CONSULTOR_MERCADO[Number(userId)] || null;
